import { Box } from "@mui/material";
import React from "react";
import { Draggable, DraggableRubric, Droppable } from "react-beautiful-dnd";
import { ITaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { CopyToolButton } from "source/components/framework/Button/CopyToolButton";
import { PopupMenu } from "source/components/framework/PopupMenu";
import { RemoveToolButton } from "source/components/framework/Button/RemoveToolButton";
import { ITaskTypeChart } from "ui/Scripts/source/models/task/taskTypeChart";
import { ChartDesignerBuilderChart } from "./ChartBuilderChart";
import { ITaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";

interface IChartDesignerBuilderChartContainerProps {
    droppableId: string;
    pickerDroppableId: string;
    draggingSource?: string;
    taskType: ITaskEditTypeItem;
    chartTestDataRows: ITaskDataRow[];
    selectedTaskTypeChart: ITaskTypeChart;
    onClick: (name: string) => void;
    onRemove: (name: string) => void;
    onCopy: (name: string) => void;
}

const getRenderItem = (charts: ITaskTypeChart[],
    selectedTaskTypeChartName: string,
    taskType: ITaskEditTypeItem,
    chartTestDataRows: ITaskDataRow[],
    onClick: (name: string) => void,
    onRemove: (name: string) => void,
    onCopy: (name: string) => void) =>
    // eslint-disable-next-line react/display-name
    (provided, _snapshot, rubric: DraggableRubric) => {
        const chart = charts[rubric.source.index];
        const isSelected = selectedTaskTypeChartName === chart.name;
        return (
            <Box
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                ref={provided.innerRef}
                width="100%"
                sx={{
                    padding: isSelected ? "0 29px" : "0 32px",
                    marginBottom: "16px"
                }}
            >
                <Box
                    sx={{
                        border: isSelected ? "4px dashed" : "1px solid",
                        borderColor: isSelected ? "text.secondary" : "divider",
                        position: "relative",
                    }}
                    onClick={(e) => {
                        onClick(chart.name);
                    }}
                >
                    <ChartDesignerBuilderChart
                        taskTypeChart={chart}
                        taskType={taskType}
                        chartTestDataRows={chartTestDataRows}
                    />
                    {isSelected &&
                        <PopupMenu>
                            <CopyToolButton
                                onClick={(_e) => onCopy(chart.name)}
                            />
                            <RemoveToolButton
                                onClick={(_e) => onRemove(chart.name)}
                            />
                        </PopupMenu>
                    }
                </Box>
            </Box>
        );
    };

export const ChartDesignerBuilderChartContainer = (props: IChartDesignerBuilderChartContainerProps) => {
    const dropAllowedFromDraggingSource = props.draggingSource?.startsWith(props.droppableId) || props.draggingSource === props.pickerDroppableId;

    return (
        <>
            <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, minHeight: "100px", marginLeft: "-16px", marginRight: "-16px", overflow: "auto" }}>
                <Droppable
                    droppableId={props.droppableId}
                    isDropDisabled={!dropAllowedFromDraggingSource}
                    renderClone={getRenderItem(props.taskType.charts, props.selectedTaskTypeChart?.name, props.taskType, props.chartTestDataRows, props.onClick, props.onRemove, props.onCopy)}
                    getContainerForClone={() => {
                        return document.getElementById("formDesignerDragItemContainer") as any;
                    }}
                >
                    {(provided, snapshot) => (
                        <Box
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                flexGrow: 0,
                                minWidth: "100%",
                                minHeight: "100px",
                                bgcolor: snapshot.isDraggingOver ? "divider" : "background.paper",
                            }}
                        >
                            {props.taskType.charts.map((chart, fieldIndex) => (
                                <Draggable
                                    key={chart.name}
                                    draggableId={`${props.droppableId}${chart.name}`}
                                    index={fieldIndex}
                                >
                                    {getRenderItem(props.taskType.charts, props.selectedTaskTypeChart?.name, props.taskType, props.chartTestDataRows, props.onClick, props.onRemove, props.onCopy)}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </Box>
                    )}
                </Droppable>
            </Box>
        </>
    );
};
